import { Immutable } from "immer";
import React, { useContext } from "react";
import { t } from "ttag";

import { Context } from "@reactivated";
import type { ComparisonBlock as Value } from "@reactivated";

import { getReactivatedContext } from "../../utils/environment";

type IProps = Immutable<{
    value: Value;
}>;

type Row = NonNullable<Value["rows"]>[number];
type Column = NonNullable<Row["column_a"]>;

const CHECKMARK_PATH = "tsicommon_cms/img/compare-checkmark2.svg";
const X_MARK_PATH = "tsicommon_cms/img/compare-x-mark2.svg";

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/comparison-block.html`` (used from the consolidated
 * sealy Toolbox component map, #33585; the standalone Django render still goes
 * through the legacy template).
 *
 * Check/X icons: the legacy ``{% static_image %}`` tag resolves the static URL
 * server-side; we rebuild it from the reactivated ``STATIC_URL`` context
 * (FinancingCardImage precedent). As with other ports, only the eager
 * (puppeteer-baseline) ``<img>`` markup is reproduced -- not the
 * production-only lazysizes plumbing.
 *
 * ``<tbody>`` is rendered explicitly: the legacy template nested ``<tr>``
 * directly in ``<table>``, but the BROWSER inserts the implicit ``<tbody>``
 * while parsing that HTML, so this explicit element produces the identical
 * final DOM (and avoids a React hydration mismatch).
 */
const ComparisonCell = (
    props: Immutable<{ column: Column | null | undefined; staticURL: string }>,
): React.JSX.Element => {
    const column = props.column;
    return (
        <td className={column?.highlight ? "highlight" : undefined}>
            {column?.text ? (
                <p>{column.text}</p>
            ) : column?.checkmark ? (
                <img
                    src={`${props.staticURL}${CHECKMARK_PATH}`}
                    className="check-mark"
                    alt={t`Checkmark Icon`}
                    loading="lazy"
                    decoding="async"
                />
            ) : column?.x_mark ? (
                <img
                    src={`${props.staticURL}${X_MARK_PATH}`}
                    className="x-mark"
                    alt={t`X Mark Icon`}
                    loading="lazy"
                    decoding="async"
                />
            ) : null}
        </td>
    );
};

export const ComparisonBlock = (props: IProps): React.JSX.Element => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const v = props.value;
    const rows = v.rows ?? [];
    return (
        <section className="comparison-block">
            <h2>{v.title}</h2>
            <p>{v.subtitle}</p>
            <table>
                <tbody>
                    <tr>
                        <th></th>
                        <th>
                            <p>{v.column_a_title}</p>
                        </th>
                        <th>
                            <p>{v.column_b_title}</p>
                        </th>
                    </tr>
                    {rows.map((row, i) => (
                        <tr key={i}>
                            <td>
                                <p className="label">{row.row_label}</p>
                            </td>
                            <ComparisonCell
                                column={row.column_a}
                                staticURL={staticURL}
                            />
                            <ComparisonCell
                                column={row.column_b}
                                staticURL={staticURL}
                            />
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
};
